import { useEffect, type ReactNode } from 'react';
import { Link, NavLink, useMatch, useParams } from 'react-router-dom';
import { useAccountAuth } from '../auth';
import { useDistrictReady } from '../district';
import { useT } from '../../i18n';
import { safeHttpUrl } from '@citizen-walk/shared-types';

/**
 * 左サイドドロワー (Phase 5 X1.5、旧 `old/apps/user/src/components/Drawer.tsx` L4 踏襲)。
 *
 * 旧の表面 API (props 構造 / nav 構造 / 「投稿アプリ ⇔ 分析アプリ」切替) を踏襲し、
 * v2 規約 (Tailwind preset semantic token / shared-ui Modal と同水準の a11y) で再実装。
 *
 * 旧と v2 の差分 (細部 UX 改善):
 *   - 旧 brand-600 → v2 primary-600 (semantic token 統一、shared-ui preset)
 *   - 旧 useTranslation (i18n) は未導入 → 日本語直書き (Phase 5 後続で i18n 検討、docs/98 別 R)
 *   - Modal pattern と同水準で Esc キー閉じる + body overflow hidden + role="dialog"
 *   - 旧 onShowDeviceTransfer / onShowCategoryMasterTable は Phase 5 後続 (placeholder)
 *
 * nav 構造 (旧踏襲):
 *   - 🏠 参加地区一覧 (/, 全 mode 共通)
 *   - 🗺 投稿アプリへ戻る / 📊 分析アプリへ (useMatch で出し分け、district.analysisEnabled 駆動)
 *   - 📥 オフラインマップ (Sprint BG-UserMenu: 下部タブ「マップ」と重複の「📍 地図表示」削除済)
 *   - 📣 お知らせ履歴 / 📋 アンケート (modal trigger callbacks)
 *   - 「データの引き継ぎ」セクション (auth 済時のみ):
 *     ⌨ ユーザー復帰コードの入力 (復帰) / 📲 ホーム画面のアプリに移す
 *     ※ 復帰コードの「表示」は ⚙ 設定 (SettingsModal) に一本化したため本セクションから撤去
 *   - ⚙ 設定 (auth 済時のみ、modal trigger callback)
 *
 * Drawer 自体は modal の open state を所有せず、AppHome / AnalysisLayout 親が所有して
 * callbacks 経由で trigger する (旧と同 pattern、modal インスタンスは親側に hoist する)。
 */

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  /** auth 済かどうか (false なら recovery / settings 系を隠す)。 */
  isAuthenticated: boolean;
  /** Modal trigger callbacks (親が modal インスタンスを所有)。 */
  onShowAnnouncements: () => void;
  onShowSurveys: () => void;
  onShowVerification: () => void;
  onShowRecover: () => void;
  onShowSettings: () => void;
  /** Sprint BG-DataSettings 8 (GAP U-C9) — デバイス間引継コード発行 modal を開く。 */
  onShowDeviceTransfer: () => void;
  /**
   * Feature — 自分の投稿写真を ZIP でまとめてダウンロードする modal を開く。
   * 未指定なら項目を表示しない (親が配線したときのみ表示)。
   */
  onShowPhotoDownload?: () => void;
  /**
   * docs/107 Wave2 / old2 AnalysisLayout.tsx:617 — AI 解析カテゴリ表 modal を開く。
   * 分析ツール (AnalysisPage) のみが渡す。未指定なら項目を表示しない (投稿アプリでは非表示)。
   */
  onShowCategoryMasterTable?: () => void;
  /**
   * docs/22 GIS (要件5) — GIS 設定 modal を開く。`district.gisEnabled` かつ本 callback 指定時のみ
   * 「🗺 GIS設定」項目を表示する (GIS 無効地区 / 未配線画面では非表示)。
   */
  onShowGisSettings?: () => void;
}

/**
 * PWA standalone 判定 (InstallPrompt.tsx と同 pattern、旧 old2 Drawer.tsx 踏襲)。
 * iOS Safari は display-mode media query 非対応のため navigator.standalone も見る。
 */
function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true;
}

const NAV_LINK_CLASS =
  'flex items-center gap-3 px-3 py-3 rounded hover:bg-slate-100 min-h-touch text-sm text-slate-800';

const NAV_LINK_ACTIVE = 'font-medium text-primary-700 bg-primary-50';

function NavButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${NAV_LINK_CLASS} w-full text-left bg-transparent border-none cursor-pointer`}
    >
      {children}
    </button>
  );
}

export function Drawer({
  open,
  onClose,
  isAuthenticated,
  onShowAnnouncements,
  onShowSurveys,
  onShowVerification,
  onShowRecover,
  onShowSettings,
  onShowDeviceTransfer,
  onShowCategoryMasterTable,
  onShowGisSettings,
  onShowPhotoDownload,
}: DrawerProps) {
  const district = useDistrictReady();
  const { urlToken } = useParams();
  // 地区「強い停止」Phase 1: アカウント (master/担当地区管理者) ログイン中なら header にラベルを出す。
  const { state: accountAuth } = useAccountAuth();
  const isAccountUser = accountAuth.status === 'authenticated' && accountAuth.role !== null;
  // Sprint BG-DataSettings 19 (U-P12): Drawer の nav label を i18n 化。
  const t = useT();
  // 厳密に分析 route 一致 (旧 R-comment: pathname.includes だと urlToken に 'analysis' を
  // 含む場合に誤判定する、useMatch 使用)
  const isInAnalysis = !!useMatch('/m/:urlToken/analysis');

  // 各 link の href を Provider 内外で組立
  // Sprint BG-UserMenu: 下部タブ「マップ」と機能重複のため「📍 地図表示」link は削除
  // (mapHref も合わせて削除)
  const mapDownloadHref = urlToken ? `/m/${urlToken}/map-download` : '/map-download';
  const analysisHref = urlToken ? `/m/${urlToken}/analysis` : null; // Provider 外は分析 link 不可
  const postsHref = urlToken ? `/m/${urlToken}` : '/';
  // Sprint BG-DataSettings 17: 使い方ガイド (U-C33)
  const guideHref = urlToken ? `/m/${urlToken}/guide` : '/guide';

  // Esc + body lock (Modal と同 pattern)
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const analysisEnabled = district.analysisEnabled ?? true;
  const analysisSiteHref = safeHttpUrl(district.analysisSiteUrl);

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} aria-hidden="true" />
      <aside
        className="fixed top-0 left-0 bottom-0 w-80 max-w-[85vw] bg-white z-50 shadow-xl overflow-auto flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label={t('drawer.menu_aria')}
      >
        <header className="p-4 bg-primary-600 text-white flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-bold truncate">{district.name}</h2>
            {/* 地区「強い停止」Phase 1: アカウント (master/担当地区管理者) ログイン中のみ表示。匿名は非表示。 */}
            {isAccountUser && (
              <span className="block text-[11px] text-white/90 mt-0.5">（管理者として利用中）</span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('drawer.close')}
            className="text-white/90 hover:text-white text-2xl leading-none min-h-touch min-w-touch shrink-0"
          >
            ×
          </button>
        </header>

        {/* old2 入口フロー移植 Phase D: 非 standalone (ホーム画面未追加) 時の iOS 7 日警告 */}
        {!detectStandalone() && (
          <div className="px-3 py-2 text-xs text-slate-600 border-b border-slate-100 leading-relaxed">
            {t('drawer.ios_standalone_warning')}
          </div>
        )}

        <nav className="p-2 flex flex-col gap-1">
          <Link to="/" className={NAV_LINK_CLASS} onClick={onClose}>
            🏠 <span>{t('drawer.home_districts')}</span>
          </Link>

          {/* 投稿/分析アプリ切替 (旧踏襲、useMatch で出し分け) */}
          {isInAnalysis ? (
            <NavLink
              to={postsHref}
              className={`${NAV_LINK_CLASS} ${NAV_LINK_ACTIVE}`}
              onClick={onClose}
            >
              🗺 <span>{t('drawer.posts_app')}</span>
            </NavLink>
          ) : (
            <>
              {analysisEnabled && analysisHref && (
                <NavLink
                  to={analysisHref}
                  className={({ isActive }) =>
                    `${NAV_LINK_CLASS}${isActive ? ` ${NAV_LINK_ACTIVE}` : ''}`
                  }
                  onClick={onClose}
                >
                  📊 <span>{t('drawer.analysis_app')}</span>
                </NavLink>
              )}
              {/* 外部サイト URL は管理者入力。http(s) 以外 (javascript: 等) はリンクにしない (docs/13 §7) */}
              {!analysisEnabled && analysisSiteHref && (
                <a
                  href={analysisSiteHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={NAV_LINK_CLASS}
                  onClick={onClose}
                >
                  📊 <span>分析サイトを開く (外部)</span>
                </a>
              )}
            </>
          )}

          <hr className="my-2 border-slate-200" />

          <Link to={mapDownloadHref} className={NAV_LINK_CLASS} onClick={onClose}>
            📥 <span>{t('drawer.offline_map')}</span>
          </Link>

          {/* Feature: 自分の投稿写真を ZIP でまとめて download (親が onShowPhotoDownload を配線したときのみ)。 */}
          {onShowPhotoDownload && (
            <NavButton
              onClick={() => {
                onShowPhotoDownload();
                onClose();
              }}
            >
              📸 <span>自分の写真をまとめてダウンロード</span>
            </NavButton>
          )}

          {/* docs/22 GIS (要件5): gisEnabled の地区でのみ GIS 設定を表示 (district は useDistrict から取得済)。 */}
          {district.gisEnabled && onShowGisSettings && (
            <NavButton
              onClick={() => {
                onShowGisSettings();
                onClose();
              }}
            >
              🗺 <span>GIS設定</span>
            </NavButton>
          )}

          <hr className="my-2 border-slate-200" />

          <NavButton
            onClick={() => {
              onShowAnnouncements();
              onClose();
            }}
          >
            📣 <span>{t('drawer.announcements')}</span>
          </NavButton>
          <NavButton
            onClick={() => {
              onShowSurveys();
              onClose();
            }}
          >
            📋 <span>{t('drawer.surveys')}</span>
          </NavButton>
          <NavButton
            onClick={() => {
              onShowVerification();
              onClose();
            }}
          >
            🤖 <span>{t('drawer.verification')}</span>
          </NavButton>

          {/* docs/107 Wave2 / old2 AnalysisLayout.tsx:617 — AI 解析カテゴリ表 (分析ツールのみ)。 */}
          {onShowCategoryMasterTable && (
            <NavButton
              onClick={() => {
                onShowCategoryMasterTable();
                onClose();
              }}
            >
              🏷 <span>AI 解析カテゴリ表</span>
            </NavButton>
          )}

          <hr className="my-2 border-slate-200" />

          {/* Sprint BG-DataSettings 17: 使い方ガイド (U-C33、旧 GuidePage modal の page 版) */}
          <Link to={guideHref} className={NAV_LINK_CLASS} onClick={onClose}>
            📘 <span>{t('drawer.guide')}</span>
          </Link>

          {isAuthenticated && (
            <>
              <hr className="my-2 border-slate-200" />
              {/* Sprint BG-UserMenu: 3 つのリカバリ系 (バックアップ / 復元 / ホーム移行) を
                  ユーザー視点で区別しやすくするため、見出し + 各 button 下に説明文を追加 */}
              <h3 className="px-3 pt-2 pb-1 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                {t('drawer.section.handoff')}
              </h3>
              {/* ユーザー復帰コードの「表示」は ⚙ 設定 (SettingsModal) に一本化した。
                  旧『🔑 ユーザー復帰コードの表示』(RecoveryCodeModal) は開くたびにコードを再発行し、
                  設定画面に表示される (= 初回保存済) コードをサーバ側で無効化する不整合があったため撤去。
                  ⌨ 入力 (復帰) と 📲 ホーム移行 はそのまま残す。 */}
              {/* old2 入口フロー移植 Phase D: 復帰コード入力は old2 の『⌨ ユーザー復帰コードの入力』
                  独立項目を踏襲 (v2 では RecoveryRecoverModal を開く onShowRecover を呼ぶ)。 */}
              <NavButton
                onClick={() => {
                  onShowRecover();
                  onClose();
                }}
              >
                ⌨ <span>{t('drawer.recover_input')}</span>
              </NavButton>
              <p className="text-[11px] text-slate-500 px-3 -mt-1 mb-1">
                {t('drawer.handoff_help.restore')}
              </p>
              <NavButton
                onClick={() => {
                  onShowDeviceTransfer();
                  onClose();
                }}
              >
                📲 <span>{t('drawer.device_transfer')}</span>
              </NavButton>
              <p className="text-[11px] text-slate-500 px-3 -mt-1 mb-1">
                {t('drawer.handoff_help.to_home')}
              </p>

              <hr className="my-2 border-slate-200" />
              <NavButton
                onClick={() => {
                  onShowSettings();
                  onClose();
                }}
              >
                ⚙ <span>{t('drawer.settings')}</span>
              </NavButton>
            </>
          )}
        </nav>

        <footer className="mt-auto p-3 text-[11px] text-slate-400 border-t border-slate-100">
          CitizenWalk v2 — Y.P.C.
        </footer>
      </aside>
    </>
  );
}

/** ハンバーガー button (Drawer trigger、AppHome / AnalysisLayout の header 左上で使用)。 */
export function DrawerToggleButton({ onClick }: { onClick: () => void }) {
  // Sprint BG-DataSettings 19 (U-P12): aria-label を i18n 化 (a11y.menu_open key)。
  const t = useT();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={t('a11y.menu_open')}
      className="inline-flex items-center justify-center text-2xl text-slate-700 hover:text-slate-900 min-h-touch min-w-touch bg-transparent border-none cursor-pointer"
    >
      ☰
    </button>
  );
}
